import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { calculateBillableUnits, type PlatformBillingBasis } from '@/lib/billableUnits';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

interface PendingWarning {
  fromUnits: number;
  toUnits: number;
  resolve: (proceed: boolean) => void;
}

/**
 * Warns before a driver or vehicle activation/deactivation would change
 * the tenant's billable base units (see billableUnits.ts) -- never blocks
 * the change, just confirms first when the bill is actually affected.
 * Counts-only, so it works without any real billing engine existing yet.
 *
 * Usage: const { checkChange, dialogProps } = useBillableUnitsGuard();
 * then `if (!(await checkChange('driver', nextActive))) return;` right
 * before the save write, and render <BillableUnitsWarningDialog {...dialogProps} />
 * somewhere in the component.
 */
export function useBillableUnitsGuard() {
  const [pending, setPending] = useState<PendingWarning | null>(null);

  const checkChange = useCallback(async (type: 'driver' | 'vehicle', nextActive: boolean): Promise<boolean> => {
    const [{ data: tenantRow }, { data: driverRows }, { data: vehicleRows }] = await Promise.all([
      supabase.from('tenants').select('platform_billing_basis').eq('id', DEFAULT_TENANT_ID).maybeSingle(),
      supabase.from('drivers').select('is_active').eq('tenant_id', DEFAULT_TENANT_ID),
      supabase.from('vehicles').select('status').eq('tenant_id', DEFAULT_TENANT_ID),
    ]);

    const basis: PlatformBillingBasis = (tenantRow?.platform_billing_basis as PlatformBillingBasis) || 'drivers';
    const activeDrivers = (driverRows || []).filter((d: { is_active: boolean }) => d.is_active).length;
    const activeVehicles = (vehicleRows || []).filter(
      (v: { status: string | null }) => (v.status || '').trim().toLowerCase() === 'active'
    ).length;

    const delta = nextActive ? 1 : -1;
    const projectedDrivers = type === 'driver' ? activeDrivers + delta : activeDrivers;
    const projectedVehicles = type === 'vehicle' ? activeVehicles + delta : activeVehicles;

    const before = calculateBillableUnits(basis, activeDrivers, activeVehicles).billableUnits;
    const after = calculateBillableUnits(basis, projectedDrivers, projectedVehicles).billableUnits;

    if (before === after) return true;

    return new Promise<boolean>((resolve) => {
      setPending({ fromUnits: before, toUnits: after, resolve });
    });
  }, []);

  const dialogProps = {
    open: !!pending,
    fromUnits: pending?.fromUnits ?? 0,
    toUnits: pending?.toUnits ?? 0,
    onConfirm: () => {
      pending?.resolve(true);
      setPending(null);
    },
    onCancel: () => {
      pending?.resolve(false);
      setPending(null);
    },
  };

  return { checkChange, dialogProps };
}
